import {
  Card,
  Dialog,
  Typography,
} from "@material-ui/core";
import { IconButton } from '@coolremote/shared';
import clsx from "clsx";
import moment from "moment-timezone";
import React, { useMemo } from "react";
import { t } from "ttag";
import { Close } from "../../icons";
import { useStoreState } from "../../models/RootStore";
import useStyles from "./AuditsList.style";
import { getActionText } from "./AuditActions";
import useTypes from "@hooks/useTypes";
import { smartStructureConvert } from "@utils/StructureConversionUtils";
import { splitCamelCase } from "@utils/StringUtils";
import { toF } from "@services/converter";
import { isNumber } from "lodash";

const dataPropertiesReplacement: { [key: string]: any } = {
  isRegistered: "Connected",
  isExpired: "Expired"
},
  notReadable = [
    "customer",
    "isAcceptedTOU",
    "_id",
    "__v",
    "user",
    "userLocationsMetadata",
    "password",
    "randId",
    "id",
    "device",
    "admin",
    "lat",
    "lon",
    "fileId",
    "type",
    "site",
    "scheduleCategory"
  ];

// tried to keep the changes as minimal to avoid regressions
// TBD: needs to be refactored also

export default function AuditsDataPopUp(props: any) {
  const allUnits = useStoreState((state) => state.units.allUnits);
  const allGroups = useStoreState((state) => state.groups.allGroups);
  const allSystems = useStoreState((s) => s.systems.allSystems);
  const allDevices = useStoreState((s) => s.devices.allDevices);
  const allCustomers = useStoreState((s) => s.customers.allCustomers);
  const getSite = useStoreState((s) => s.sites.getSite);

  const { hvacBrands, auditActionTitles } = useTypes()

  const hvacBrandsMap: any = useMemo(() => smartStructureConvert(hvacBrands, 'value').object, [hvacBrands]);

  const { audit, roles, schedules, changeovers, traps, users } = props,
    { schedule, changeover, trap, source, sourceType, customer, action: actionId, actor: user, site, device, unit, bacnetUnit, modbusUnit, line, value: auditValue, data = {}, unitName } = audit,
    resolvedData = { ...data },
    { operationStatuses, operationModesExtended, fanModes, swingModes, temperatureScale, actionSources, curveTempModes } = useStoreState((s) => s.types),
    classes = useStyles(),
    timeFormat = useStoreState((state) => state.users.timeFormat),
    temperatureScaleMirror = useStoreState((s: any) => s.temperatureScaleMirror),
    userTemperatureScale = useStoreState((s: any) => s.users.me.temperatureScale || 1),
    isCelsius = +temperatureScaleMirror.celsius === +userTemperatureScale,

    valueMapping = (value: number, action: string) => {
      const lowerCaseAction = action.toLowerCase();
      if (action === "setUnitEWrcEnableSetpoint") {
         return !!value ? t`TRUE` : t`FALSE`; 
      }
      if (lowerCaseAction.includes("setpoint") && isNumber(value)) {
        return isCelsius ? Math.round(value) + t`°C` : Math.round(toF(value)) + t`°F`;
      }
      if (lowerCaseAction.includes("operationstatus")) { return operationStatuses[value] || value; }
      if (lowerCaseAction.includes("operationmode")) { return operationModesExtended[value] || value; }
      if (lowerCaseAction.includes("fanmode")) { return fanModes[value] || value; }
      if (lowerCaseAction.includes("swingmode")) { return swingModes[value] || value; }
      if (lowerCaseAction.includes("tempcurve")) { return `${curveTempModes[value] || value} (${value})`; }
      return value;
    },
    valueTitleMapping = (action: string) => {
      const lowerCaseAction = action.toLowerCase();
      if (lowerCaseAction.includes("setpoint")) { return t`Setpoint`; }
      if (lowerCaseAction.includes("operationstatus")) { return t`Operation Status`; }
      if (lowerCaseAction.includes("operationmode")) { return t`Operation Mode`; }
      if (lowerCaseAction.includes("fanmode")) { return t`Fan Mode`; }
      if (lowerCaseAction.includes("swingmode")) { return t`Swing Mode`; }
      return t`value`;
    },
    replaceTitle = (property: string) => {
      const replacement = dataPropertiesReplacement[property];
      if (replacement) { return replacement; }
      return splitCamelCase(property);
    },

    convertDate = () => {
      const { timestamp } = audit;
      const timezone = getSite(site)?.timezone || '';
      const date = timezone ? moment(timestamp).tz(timezone).format(`ll ${timeFormat}`) : moment(timestamp).format(`ll ${timeFormat}`);
      return date;
    },
    DataRow = ({ property, content, action, index }: any) => {
      const timezone = getSite(site)?.timezone || '';
      const contentText = typeof content === "boolean" ? (content ? "yes" : "no") : content;
      return (
        <div className={clsx(classes.mainRow, { [classes.grayRow]: index % 2 })}>
          <Typography className={classes.rowTitle}>
            {property === "value" ? valueTitleMapping(action) : replaceTitle(property)}
          </Typography>
          <Typography className={classes.rowData}>
            {property === "value" ? `${valueMapping(contentText, action)}`
              : property === "temperatureScale" ? `${temperatureScale[contentText]}`
                : property === "unit" ? `${allUnits[contentText]?.name || ""}`
                  : property === "system" ? `${allSystems[contentText]?.name || ""}`
                    : property === "group" ? `${allGroups[contentText]?.name || ""}`
                      : property === "expiryTimestamp" ? `${moment(contentText * 1000).tz(timezone).format(`ll ${timeFormat}`) || moment(contentText).format(`ll ${timeFormat}`)}`
                        : property === "role" ? `${roles[contentText]?.label || "-"}`
                          : `${contentText}`}
          </Typography>
        </div>
      );
    },
    MainDataRow = ({ title, content = '', index, jsxContent = null }: any) => (
      <div className={clsx(classes.mainRow, { [classes.grayRow]: index % 2 })}>
        <Typography className={classes.rowTitle}>{`${title}`}</Typography>
        {!jsxContent ? <Typography className={classes.rowData}>{`${content}`}</Typography> : null}
        {jsxContent ? <Typography className={classes.rowData}>{jsxContent}</Typography> : null}
      </div>
    ),
    getTimeFromMins = (mins: number) => {

      const h = (mins / 60) || 0,
        m = (mins % 60) || 0;
      return moment.utc().hours(h).minutes(m).format("hh:mm A");
    };
  let countedIndex = 0;
  const auditSource = (source: number) => {
    switch (source) {
      case +actionSources.user: return t`Application User`;
      case +actionSources.schedule: return t`Scheduled Operation`;
      case +actionSources.homeAutomation: return t`Home automation / BMS`;
      case +actionSources.hvac: return t`HVAC`;
      case +actionSources.applicationLogic: return t`Application Logic`;
      case +actionSources.ecobee: return t`Ecobee`;
      case +actionSources.deviceLCD: return t`CA Device LCD`;
      default: return t`Unknown source`;
    }
  };

  if (source === +actionSources.schedule || source === +actionSources.applicationLogic) {
    // These are user-typed names, not programmatic identifiers - splitCamelCase
    // mangles names that happen to have no spaces (e.g. "tURNoN" -> "T U R No N").
    if (schedule) {
      resolvedData.scheduleName = String(schedules[schedule]?.name || resolvedData.scheduleName || schedule || '');
    }

    if (changeover) {
      resolvedData.ruleName = String(changeovers[changeover]?.name || resolvedData.ruleName || changeover || '');
    }

    if (trap) {
      resolvedData.ruleName = String(traps[trap]?.name || resolvedData.ruleName || trap || '');
    }
  }

  const renderFilters = (mainIndex: number) => {

    let runningIndex = mainIndex || 0;

    if (!data?.filters || !Array.isArray(data?.filters)) {
      return null;
    }
    return data.filters.map((filter: any, index: number) => {
      const title = t`Filter ` + (index + 1);
      const type = filter.type === 'both' ? t`Indoor & Outdoor` : filter.type.charAt(0).toUpperCase() + filter.type.slice(1);
      const brand = hvacBrandsMap[filter.brand]?.name || t`Unknown Brand`;
      const content = (
        <div className={classes.filterListContainer}>
          <Typography><b >{t`Brand: `}</b>{brand}</Typography>
          <Typography><b>{t`Type: `}</b>{type}</Typography>
          <Typography className={classes.codesList}><b>{t`Codes: `}</b><span title={filter.codes}>{filter.codes}</span></Typography>
        </div>
      )

      return (
        <MainDataRow
          key={`filter-${index}`}
          title={title}
          jsxContent={content}
          index={runningIndex++}
        />
      )
    })

  }

  return (
    <div>
      <Dialog
        open={true}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <div className={classes.titleContainer}>
          <Typography className={classes.headerTitle}>{t`Audit Data`}</Typography>
          <IconButton disableRipple onClick={() => props.setIsSeen(null)} className={classes.iconBtnStyle}>
            <Close color="#7f7692" />
          </IconButton>
        </div>
        <div className={classes.dialogContent}>
          <Card className={classes.container}>
            <MainDataRow title={t`Source`} content={auditSource(+source)} index={countedIndex++} />
            <MainDataRow title={t`Source Type`} content={sourceType || "_"} index={countedIndex++} />
            <MainDataRow title={t`Customer`} content={allCustomers[customer]?.name || '-'} index={countedIndex++} />
            <MainDataRow title={t`Action`} content={getActionText(actionId, auditActionTitles)} index={countedIndex++} />
            <MainDataRow title={t`Date`} content={convertDate()} index={countedIndex++} />
            <MainDataRow title={t`User`} content={users[user]?.username || '-'} index={countedIndex++} />
            {site && <MainDataRow title={t`Site`} content={getSite(site)?.name || '-'} index={countedIndex++} />}
            {device && <MainDataRow title={t`Device`} content={allDevices[device]?.name || '-'} index={countedIndex++} />}
            {unit && <MainDataRow title={t`Unit`} content={unitName || allUnits[unit]?.name || '-'} index={countedIndex++} />}
            {bacnetUnit && <MainDataRow title={t`BACnet Unit`} content={resolvedData.unitName || '-'} index={countedIndex++} />}
            {modbusUnit && <MainDataRow title={t`Modbus Unit`} content={resolvedData.unitName || '-'} index={countedIndex++} />}
            {line && <MainDataRow title={t`Line`} content={line} index={countedIndex++} />}
            {auditValue !== undefined && (
              <MainDataRow title={t`Value`} content={valueMapping(auditValue as number, actionId)} index={countedIndex++} />
            )}
            {resolvedData &&
              Object.keys(resolvedData).map((key: any) => {
                let content = resolvedData[key];
                if (
                  typeof content !== "string" &&
                  typeof content !== "number" &&
                  typeof content !== "boolean"
                ) {
                  return null;
                }

                if (notReadable.includes(key)) {
                  return null;
                }
                if (typeof content === "number" && (key === "powerOffTime" || key === "powerOnTime")) {
                  content = getTimeFromMins(content);
                }
                if ((bacnetUnit || modbusUnit) && key === 'unitName') {
                  return null;
                }
                return (
                  <DataRow
                    key={`audit-data-${countedIndex}`}
                    property={key}
                    content={content}
                    action={actionId}
                    index={countedIndex++}
                  />
                );
              })}
            {renderFilters(countedIndex)}
          </Card>
        </div>
      </Dialog>
    </div>
  );
}
